// Mentor portal layout — minimal nav. Day/night via data-mentor-theme
// (PRD §11 + house rule "every portal supports day/night").
import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { Compass, Inbox, Briefcase, LogOut, Sun, Moon, Lightbulb } from "lucide-react";
import { useThemeMode } from "@/lib/use-theme-mode";

const BASE = import.meta.env.BASE_URL;

export default function MentorLayout({ children }: { children: ReactNode }) {
  const [, setLocation] = useLocation();
  const [loc] = useLocation();
  const { mode, toggle } = useThemeMode("mentor", "dark");
  const [name, setName] = useState<string>("");

  useEffect(() => {
    const tok = localStorage.getItem("mentor_token");
    if (!tok) { setLocation("/mentor/login"); return; }
    fetch(`${BASE}api/mentors/me`, { headers: { "x-mentor-token": tok } })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then((d) => {
        setName(d.mentor?.fullName || "");
        if (d.needsActivation) setLocation("/mentor/activate");
      })
      .catch(() => { localStorage.removeItem("mentor_token"); setLocation("/mentor/login"); });
  }, [setLocation]);

  function logout() {
    localStorage.removeItem("mentor_token");
    setLocation("/mentor/login");
  }

  // wouter v3: <Link> renders the <a> itself. Wrapping a child <a> creates
  // an invalid <a><a> tree and triggers a React hydration error that blanks
  // the wrapped section. Pass className/testId/href directly to Link.
  const navItem = (href: string, icon: ReactNode, label: string) => {
    const active = loc === href || loc.startsWith(href + "/");
    return (
      <Link href={href}
        className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${active ? "bg-[var(--mentor-nav-active)] text-[var(--mentor-fg)] font-medium" : "text-[var(--mentor-fg-muted)] hover:bg-[var(--mentor-nav-hover)] hover:text-[var(--mentor-fg)]"}`}
        data-testid={`nav-mentor-${label.toLowerCase().replace(/\s+/g, "-")}`}>
        {icon}<span>{label}</span>
      </Link>
    );
  };

  return (
    <div data-mentor-theme={mode} className="min-h-screen bg-[var(--mentor-bg)] text-[var(--mentor-fg)]">
      <header className="border-b border-[var(--mentor-border)] sticky top-0 z-20 bg-[var(--mentor-bg)]/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link href="/mentor" className="text-base font-bold tracking-tight">Favcy Select · Mentor</Link>
            <nav className="flex items-center gap-1">
              {navItem("/mentor", <Compass className="w-4 h-4" />, "Browse")}
              {navItem("/mentor/interests", <Inbox className="w-4 h-4" />, "My interests")}
              {navItem("/mentor/proposals", <Lightbulb className="w-4 h-4" />, "My proposals")}
              {navItem("/mentor/engagements", <Briefcase className="w-4 h-4" />, "Engagements")}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={toggle} aria-label="Toggle day/night" className="p-2 rounded-md hover:bg-[var(--mentor-nav-hover)]" data-testid="button-mentor-theme-toggle">
              {mode === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <span className="text-xs text-[var(--mentor-fg-muted)] hidden sm:inline">{name}</span>
            <button onClick={logout} className="flex items-center gap-1.5 text-xs text-[var(--mentor-fg-muted)] hover:text-[var(--mentor-fg)]" data-testid="button-mentor-logout">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>
      <main>{children}</main>
    </div>
  );
}
